import {
  listAttachments,
  removeIframe,
  raiseAttachmentItem,
  updateIframe,
} from '../../../helpers/helpers';
import { useState, useEffect, useRef } from 'react';
import { config } from '../../../config';
import { useLogger } from '@ui/shared/components/Logger';
import {
  Menu,
  TruncateText,
  ContextView,
  CloseButton,
  Flex,
  Spinner,
  Text,
  Link,
} from '@smarterservices/smarter-ui';
import { ParamsNotFoundError } from '../../../helpers/definedErrors';

const AttachmentMenu = () => {
  const [attachments, setAttachments] = useState(null);
  const [loadingAttachments, setLoadingAttachments] = useState(true);
  const [errorFetchingAttachments, setErrorFetchingAttachments] = useState(false);
  const contextViewRef = useRef(null);
  const [attachmentParams, setAttachmentParams] = useState(null);
  const logger = useLogger();

  const fetchAttachments = async () => {
    setLoadingAttachments(true);
    setErrorFetchingAttachments(false);
    try {
      const attachments = await listAttachments(attachmentParams);
      setAttachments(attachments);      
      setErrorFetchingAttachments(false);
      setLoadingAttachments(false);
    } catch (error) {
      setErrorFetchingAttachments(true);
      setLoadingAttachments(false);
      // Log there was an error...
      logger.logError("Attachments", 'Error loading attachments:', error);
    }
  };

  useEffect(() => {    

    const rootElement = document.getElementById('root');
    rootElement.style.background = 'transparent';
    const bodyElement = document.body;
    bodyElement.style.background = 'transparent';
    const url = new URL(window.location.href);
    const attachmentUrlParams = new Proxy(new URLSearchParams(url.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    });
    if (
      !attachmentUrlParams ||
      !attachmentUrlParams.sessionSid ||
      !attachmentUrlParams.installSid ||
      !attachmentUrlParams.examSid ||
      !attachmentUrlParams.iframeId ||
      !attachmentUrlParams.token
    ) {
      throw new ParamsNotFoundError();
    }

    const _attachmentParams = {
      sessionSid: attachmentUrlParams.sessionSid,
      installSid: attachmentUrlParams.installSid,
      examSid: attachmentUrlParams.examSid,
      iframeId: attachmentUrlParams.iframeId,
      token: attachmentUrlParams.token
    }  
    setAttachmentParams(_attachmentParams);
  }, []);

  useEffect(() => {    
    if(attachmentParams){
      fetchAttachments();
    }    
  }, [attachmentParams]);

  useEffect(() => {
    if (!contextViewRef.current || !attachmentParams) return;
    const height = contextViewRef.current?.ref?.offsetHeight;
    updateIframe(attachmentParams.iframeId, { height: `${height}px` });
  }, [contextViewRef.current, loadingAttachments, attachmentParams]);

  const hasAttachments = attachments && attachments.length > 0;

  return (
    <ContextView
      as="div"
      placement="bottom start"
      borderColor="secondary"
      ref={(el) => {
        contextViewRef.current = el;
      }}
    >
      <Menu
        as="div"
        defaultShow={true}
        themeOverride={{
          maxWidth: '100%',
          focusBorderStyle: 'none',
        }}
      >
        {/* Available Attachments */}
        {(!loadingAttachments && hasAttachments && !errorFetchingAttachments) && (
          <Menu.Group label="Available Attachments">
            <CloseButton
              placement="end"
              screenReaderLabel="Close Attachment List"
              onClick={() => removeIframe(attachmentParams.iframeId)}
            />
            <Menu.Separator />
            {attachments.map((attachment, index) => (
              <Menu.Item
                key={index}
                onClick={() => {
                  raiseAttachmentItem(
                    attachment.sid,
                    attachment.name,
                    config.session.sessionUiUrl
                  );
                  removeIframe(attachmentParams.iframeId);
                }}
              >
                <TruncateText>{attachment.name}</TruncateText>
              </Menu.Item>
            ))}
          </Menu.Group>
        )}
        {/* No Attachments Available */}
        {(!loadingAttachments && !hasAttachments && !errorFetchingAttachments) && (
          <Menu.Group label="No Attachments Available">
            <CloseButton
              placement="end"
              screenReaderLabel="Close Attachment List"
              onClick={() => removeIframe(attachmentParams.iframeId)}
            />
          </Menu.Group>
        )}

        {/* Loading Attachments */}
        {loadingAttachments && (
          <Menu.Group label={
            <Flex>
              <Spinner renderTitle="Loading Attachments" size="x-small" margin="0 small 0 0" />  
              <Text as="span" size="small" color="primary">
                Loading Attachments
              </Text>
            </Flex>
          }>
            <CloseButton
              placement="end"
              screenReaderLabel="Close Attachment List"
              onClick={() => removeIframe(attachmentParams.iframeId)}
            />
          </Menu.Group>
        )}

        {/* Error Fetching Attachments */}
        {errorFetchingAttachments && (
          <Menu.Group label={
            <Flex>
              <Text as="span" size="small" color="primary">
                Error Loading Attachments <Link onClick={() => fetchAttachments()}>Try Again</Link>
              </Text>
            </Flex>
          }>
            <CloseButton
              placement="end"
              screenReaderLabel="Close Attachment List"
              onClick={() => removeIframe(attachmentParams.iframeId)}
            />
          </Menu.Group>
        )}
      </Menu>
    </ContextView>
  );
};

export default AttachmentMenu;
